/**
 * EmbedCard Component
 * Module 07 - Dashboard UI
 * Section 06 - Embeds Management (Step 6.3)
 * 
 * Individual embed card display with preview, status, and actions
 */

import React, { useState } from 'react';
import { Code, Trash2, Eye, Copy, MoreVertical, Power, PowerOff, RotateCcw } from 'lucide-react';
import { Menu } from '@headlessui/react';
import { Embed } from '../../types';
import { formatDate } from '../../utils';
import Button from '../common/Button';
import { cn } from '../../utils';

interface EmbedCardProps {
  embed: Embed;
  onDelete: (id: string) => void;
  onEdit: (embed: Embed) => void;
  onViewCode: (embed: Embed) => void;
  onPreview: (embed: Embed) => void;
  onToggleStatus: (id: string, isActive: boolean) => void;
  onResetViewCount: (id: string) => void;
}

/**
 * EmbedCard displays an individual embed with actions
 * Shows preview, metadata, and action buttons
 */
const EmbedCard: React.FC<EmbedCardProps> = ({
  embed,
  onDelete,
  onEdit,
  onViewCode,
  onPreview,
  onToggleStatus,
  onResetViewCount,
}) => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [confirmingReset, setConfirmingReset] = useState(false);

  return (
    <div className="card hover:shadow-md transition-shadow duration-200 animate-fade-in">
      {/* Preview Area */}
      <div 
        className="relative aspect-video mb-4 bg-gray-100 rounded-lg overflow-hidden cursor-pointer group"
        onClick={() => onPreview(embed)}
      >
        {embed.posterImage ? (
          <img
            src={embed.posterImage}
            alt={embed.name}
            className="w-full h-full object-cover"
            onError={(e) => {
              e.currentTarget.style.display = 'none';
            }}
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-primary-50 to-primary-100">
            <Code className="h-16 w-16 text-primary-300 group-hover:text-primary-400 transition-colors" />
          </div>
        )}
        
        {/* Overlay on hover */}
        <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-40 transition-all duration-200 flex items-center justify-center">
          <div className="opacity-0 group-hover:opacity-100 transition-opacity duration-200">
            <Eye className="h-8 w-8 text-white" />
          </div>
        </div>

        {/* Status Badge */}
        <div className="absolute top-2 right-2">
          <span
            className={cn(
              'px-2 py-1 rounded-full text-xs font-semibold',
              embed.isActive
                ? 'bg-green-100 text-green-800'
                : 'bg-gray-100 text-gray-800'
            )}
          >
            {embed.isActive ? 'Active' : 'Inactive'}
          </span>
        </div>
      </div>

      {/* Embed Info */}
      <div className="space-y-3">
        <div>
          <h3 className="font-semibold text-gray-900 truncate" title={embed.name}>
            {embed.name}
          </h3>
          <p className="text-sm text-gray-500 truncate mt-1">
            Model ID: {embed.modelId.slice(0, 8)}...
          </p>
        </div>

        {/* Metadata */}
        <div className="text-sm text-gray-500 space-y-1">
          <div className="flex items-center justify-between">
            <span>Views:</span>
            <span className="font-medium text-gray-700">{embed.viewCount.toLocaleString()}</span>
          </div>
          <div className="flex items-center justify-between">
            <span>Domains:</span>
            <span className="font-medium text-gray-700">{embed.allowedDomains.length}</span>
          </div>
          <div className="flex items-center justify-between">
            <span>Created:</span>
            <span className="font-medium text-gray-700">{formatDate(embed.createdAt)}</span>
          </div>
        </div>

        {/* Domains Preview */}
        <div className="pt-2 border-t border-gray-200">
          <p className="text-xs text-gray-500 mb-1">Allowed Domains:</p>
          <div className="flex flex-wrap gap-1">
            {embed.allowedDomains.slice(0, 2).map((domain, idx) => (
              <span
                key={idx}
                className="text-xs bg-gray-100 text-gray-700 px-2 py-0.5 rounded"
              >
                {domain}
              </span>
            ))}
            {embed.allowedDomains.length > 2 && (
              <span className="text-xs text-gray-500">
                +{embed.allowedDomains.length - 2} more
              </span>
            )}
          </div>
        </div>
      </div>

      {/* Actions */}
      <div className="mt-4 flex gap-2">
        <Button
          variant="primary"
          size="sm"
          className="flex-1"
          onClick={() => onViewCode(embed)}
        >
          <Copy className="h-4 w-4 mr-1" />
          Get Code
        </Button>
        
        <Button
          variant="secondary"
          size="sm"
          onClick={() => onPreview(embed)}
        >
          <Eye className="h-4 w-4" />
        </Button>

        {/* More Actions Menu */}
        <Menu as="div" className="relative">
          <Menu.Button
            as={Button}
            variant="secondary"
            size="sm"
            aria-label="More actions"
            onClick={() => setIsMenuOpen(!isMenuOpen)}
          >
            <MoreVertical className="h-4 w-4" />
          </Menu.Button>

          <Menu.Items className="absolute right-0 mt-2 w-48 origin-top-right rounded-md bg-white py-1 shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none z-10">
            <Menu.Item>
              {({ active }) => (
                <button
                  onClick={() => onToggleStatus(embed.id, !embed.isActive)}
                  className={cn(
                    'flex w-full items-center gap-2 px-4 py-2 text-sm',
                    active ? 'bg-gray-100' : '',
                    'text-gray-700'
                  )}
                >
                  {embed.isActive ? (
                    <>
                      <PowerOff className="h-4 w-4" />
                      Deactivate
                    </>
                  ) : (
                    <>
                      <Power className="h-4 w-4" />
                      Activate
                    </>
                  )}
                </button>
              )}
            </Menu.Item>

            <Menu.Item>
              {({ active }) => (
                <button
                  onClick={() => onEdit(embed)}
                  className={cn(
                    'flex w-full items-center gap-2 px-4 py-2 text-sm',
                    active ? 'bg-gray-100' : '',
                    'text-gray-700'
                  )}
                >
                  <Code className="h-4 w-4" />
                  Edit Configuration
                </button>
              )}
            </Menu.Item>

            <Menu.Item>
              {({ active }) => (
                <button
                  onClick={() => setConfirmingReset(true)}
                  className={cn(
                    'flex w-full items-center gap-2 px-4 py-2 text-sm',
                    active ? 'bg-gray-100' : '',
                    'text-gray-700'
                  )}
                >
                  <RotateCcw className="h-4 w-4" />
                  Reset view count
                </button>
              )}
            </Menu.Item>

            <Menu.Item>
              {({ active }) => (
                <button
                  onClick={() => onDelete(embed.id)}
                  className={cn(
                    'flex w-full items-center gap-2 px-4 py-2 text-sm',
                    active ? 'bg-red-50' : '',
                    'text-red-600'
                  )}
                >
                  <Trash2 className="h-4 w-4" />
                  Delete
                </button>
              )}
            </Menu.Item>
          </Menu.Items>
        </Menu>
      </div>

      {/* Inline reset view count confirmation */}
      {confirmingReset && (
        <div className="mt-3 rounded-md border border-amber-200 bg-amber-50 px-4 py-3">
          <p className="text-sm text-amber-800 mb-3">Reset view count to 0?</p>
          <div className="flex gap-2">
            <Button
              variant="primary"
              size="sm"
              onClick={() => {
                onResetViewCount(embed.id);
                setConfirmingReset(false);
              }}
            >
              Confirm reset
            </Button>
            <Button
              variant="secondary"
              size="sm"
              onClick={() => setConfirmingReset(false)}
            >
              Cancel
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

export default EmbedCard;
